import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Copy, Trash2, ToggleLeft, ToggleRight, Link2, ExternalLink } from 'lucide-react';

const API = `${(window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "")}/api`;
const BASE_URL = window.location.origin;

export default function ScoutCodes() {
  const { token } = useAuth();
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ code: '', scout_user_id: '', scout_name: '', description: '' });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCodes = async () => {
    try {
      const res = await axios.get(`${API}/scout-codes`, { headers });
      setCodes(res.data);
    } catch (err) {
      toast.error('Error al cargar codigos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/auth/users`, { headers });
      setUsers(res.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchCodes();
    fetchUsers();
  }, []);

  const handleCreate = async () => {
    if (!form.code.trim() || !form.scout_user_id) {
      toast.error('Codigo y Scout son obligatorios');
      return;
    }
    try {
      await axios.post(`${API}/scout-codes`, form, { headers });
      toast.success('Codigo creado');
      setShowCreate(false);
      setForm({ code: '', scout_user_id: '', scout_name: '', description: '' });
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear');
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API}/scout-codes/${id}/toggle`, {}, { headers });
      fetchCodes();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este codigo?')) return;
    try {
      await axios.delete(`${API}/scout-codes/${id}`, { headers });
      toast.success('Eliminado');
      fetchCodes();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const copyLink = (code) => {
    const url = `${BASE_URL}/formulario`;
    navigator.clipboard.writeText(`Enlace: ${url}\nCodigo: ${code}`);
    toast.success('Enlace y codigo copiados al portapapeles');
  };

  const handleUserSelect = (e) => {
    const userId = e.target.value;
    const user = users.find(u => u.id === userId);
    setForm(prev => ({
      ...prev,
      scout_user_id: userId,
      scout_name: user ? (user.full_name || user.username) : ''
    }));
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="scout-codes-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Codigos de Formulario</h1>
            <p className="text-sm text-gray-500 mt-1">Genera enlaces unicos para que los jugadores completen sus datos</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-code-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Codigo
          </Button>
        </div>

        {/* Codes List */}
        <div className="grid gap-4">
          {codes.map(c => (
            <Card key={c.id} data-testid={`code-card-${c.code}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                      <Link2 className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-gray-900">{c.code}</span>
                        <Badge variant={c.is_active ? "default" : "secondary"}>
                          {c.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">
                        Scout: {c.scout_name} {c.description && `- ${c.description}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{c.uses_count} uso(s)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => copyLink(c.code)} data-testid={`copy-link-${c.code}`}>
                      <Copy className="w-4 h-4 mr-1" /> Copiar enlace
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.open(`/formulario`, '_blank')} data-testid={`preview-${c.code}`}>
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleToggle(c.id)} data-testid={`toggle-${c.code}`}>
                      {c.is_active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} data-testid={`delete-${c.code}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {!loading && codes.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                No hay codigos creados. Crea uno para compartir el formulario.
              </CardContent>
            </Card>
          )}
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent data-testid="create-code-dialog">
            <DialogHeader>
              <DialogTitle>Nuevo Codigo de Formulario</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Codigo (clave unica)</Label>
                <Input
                  value={form.code}
                  onChange={e => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s/g, '') }))}
                  placeholder="Ej: CODE2026"
                  data-testid="input-code"
                />
              </div>
              <div>
                <Label>Scout / Representante</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  value={form.scout_user_id}
                  onChange={handleUserSelect}
                  data-testid="select-scout"
                >
                  <option value="">Seleccionar...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Descripcion (opcional)</Label>
                <Input
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Ej: Para jugadores Sub-15"
                  data-testid="input-description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={handleCreate} data-testid="confirm-create-btn">Crear Codigo</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
